import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';
import { initialsOf } from '@/components/projects/PeoplePicker';
import {
  TICKET_PRIORITY_META,
  TICKET_STATUS_META,
  personLabel,
  ticketCategoryIcon,
  ticketRef,
} from '@/features/tickets/model';
import type { TicketPerson } from '@/features/tickets/hooks';
import type { TicketPriority, TicketStatus } from '@/types/database';

/** Small shared chips so the inbox table, mobile cards, drawer and
 *  notifications all render a ticket the same way. */

export function TicketStatusChip({
  status,
  className,
}: {
  status: TicketStatus;
  className?: string;
}) {
  const meta = TICKET_STATUS_META[status];
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        meta.className,
        className,
      )}
    >
      {meta.label}
    </span>
  );
}

export function TicketPriorityChip({
  priority,
  className,
}: {
  priority: TicketPriority;
  className?: string;
}) {
  const meta = TICKET_PRIORITY_META[priority];
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        meta.className,
        className,
      )}
    >
      <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  );
}

export function TicketRef({ number, className }: { number: number; className?: string }) {
  return (
    <span className={cn('font-mono text-xs text-muted-foreground', className)}>
      {ticketRef(number)}
    </span>
  );
}

export function TicketCategoryLabel({
  category,
  className,
}: {
  category: { name: string; slug: string } | null | undefined;
  className?: string;
}) {
  const Icon = ticketCategoryIcon(category?.slug);
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5 text-sm', className)}>
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.7} />
      <span className="truncate">{category?.name ?? 'Uncategorised'}</span>
    </span>
  );
}

export function PersonChip({
  person,
  fallback = 'Unassigned',
  size = 'sm',
  className,
  muted,
}: {
  person: TicketPerson | null | undefined;
  fallback?: string;
  size?: 'xs' | 'sm';
  className?: string;
  muted?: boolean;
}) {
  const dim = size === 'xs' ? 'h-5 w-5 text-[9px]' : 'h-6 w-6 text-[10px]';
  if (!person) {
    return (
      <span className={cn('inline-flex items-center gap-2 text-sm text-muted-foreground', className)}>
        <span
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-border',
            dim,
          )}
          aria-hidden
        />
        <span className="truncate">{fallback}</span>
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex min-w-0 items-center gap-2 text-sm',
        muted ? 'text-muted-foreground' : 'text-foreground',
        className,
      )}
    >
      <Avatar className={cn('shrink-0', dim)}>
        {person.avatar_url && <AvatarImage src={person.avatar_url} alt="" />}
        <AvatarFallback className={size === 'xs' ? 'text-[9px]' : 'text-[10px]'}>
          {initialsOf(person)}
        </AvatarFallback>
      </Avatar>
      <span className="truncate">{personLabel(person)}</span>
    </span>
  );
}
